/*
 * Drawer (Sheet): a panel sliding in from the end side (or the start side for navigation) for detail views
 * and longer forms that keep the page in context.
 *   <Drawer open={open} onOpenChange={setOpen} title={…} footer={…}>…</Drawer>
 */
import { X } from '@phosphor-icons/react'
import { Dialog as D } from 'radix-ui'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'

export function Drawer({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  children,
  footer,
  side = 'end',
  size = 'md',
  hideTitle,
  className,
}: {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  trigger?: ReactNode
  title: ReactNode
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
  side?: 'start' | 'end'
  size?: 'sm' | 'md' | 'lg'
  /** keep the title for screen readers only (navigation drawers) */
  hideTitle?: boolean
  className?: string
}) {
  const { t } = useTranslation('common')
  const width = { sm: 'max-w-xs', md: 'max-w-md', lg: 'max-w-2xl' }[size]
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      {trigger ? <D.Trigger asChild>{trigger}</D.Trigger> : null}
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 bg-scrim data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in" />
        <D.Content
          className={cn(
            'fixed inset-y-0 z-50 flex w-[92vw] flex-col bg-surface shadow-float outline-none',
            side === 'end'
              ? 'end-0 data-[state=closed]:animate-slide-out-end data-[state=open]:animate-slide-in-end'
              : 'start-0 data-[state=closed]:animate-slide-out-start data-[state=open]:animate-slide-in-start',
            width,
            className,
          )}
        >
          <div
            className={cn(
              'flex items-start gap-4 border-b border-hairline px-5 py-4',
              hideTitle && 'sr-only',
            )}
          >
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <D.Title className="text-17 font-semibold text-ink">{title}</D.Title>
              {description ? (
                <D.Description className="text-13 text-muted">{description}</D.Description>
              ) : null}
            </div>
            <D.Close
              aria-label={t('actions.close')}
              className="-me-2 grid size-9 shrink-0 place-items-center rounded-xl text-muted hover:bg-control hover:text-ink touch:size-11"
            >
              <X className="size-[18px]" aria-hidden="true" />
            </D.Close>
          </div>
          {!description ? <D.Description className="sr-only">{title}</D.Description> : null}
          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
          {footer ? (
            <div className="flex items-center justify-end gap-2 border-t border-hairline px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              {footer}
            </div>
          ) : null}
        </D.Content>
      </D.Portal>
    </D.Root>
  )
}

export const DrawerClose = D.Close
